import { useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  TableBody,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import { getMyEmissionsDisclosure } from "../../../service/cat15.service";
import {
  DqsLevel,
  MyDisclosureRow,
  MyDisclosureView,
} from "../../../models/dto/cat15/cat15.types";
import {
  FeCell,
  FeHeadCell,
  FeRow,
  FeTable,
  FeTableScroll,
} from "../../../components/cat15/FeTable";
import FePagination from "../../../components/cat15/FePagination";
import MyDataCertModal from "./MyDataCertModal";
import { formatNumber } from "../format";

const toDmy = (iso: string) => {
  const [y, m, d] = (iso || "").split("-");
  return y && m && d ? `${d}/${m}/${y}` : "—";
};

const initials = (name?: string) =>
  (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 3)
    .toUpperCase() || "—";

/** DQS pill — Figma: green3 bg, bold 13, radius 6, h24. */
const DqsBadge = ({ dqs, label }: { dqs: DqsLevel; label: string }) => (
  <Box
    component="span"
    display="inline-flex"
    alignItems="center"
    height="24px"
    px="8px"
    borderRadius="6px"
    bgcolor="var(--green3)"
    color="var(--green)"
    sx={{ fontSize: "13px", fontWeight: 700 }}
  >
    {label} {dqs}
  </Box>
);

/**
 * Borrower My Data — read-only view of the borrower's PCAF data as entered by
 * their lender (Figma node 6196:10089). Banner + lender chip + a searchable /
 * filterable disclosure table.
 */
const MyData = () => {
  const { t } = useTranslation([financedEmissions, common], { nsMode: "fallback" });
  const [data, setData] = useState<MyDisclosureView | undefined>();
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState<"all" | number>("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [detailRow, setDetailRow] = useState<MyDisclosureRow | null>(null);

  useEffect(() => {
    getMyEmissionsDisclosure().then(setData);
  }, []);

  const rows: MyDisclosureRow[] = useMemo(() => data?.rows ?? [], [data]);

  const years = useMemo(
    () => Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => b - a),
    [rows],
  );

  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (yearFilter === "all" || r.year === yearFilter) &&
          (!search.trim() || String(r.year).includes(search.trim())),
      ),
    [rows, yearFilter, search],
  );

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box display="flex" flexDirection="column" gap="20px">
      {/* Banner — data owned by the lender, read-only. */}
      <Box
        display="flex"
        alignItems="center"
        gap="8px"
        bgcolor="var(--green3)"
        borderRadius="8px"
        px="12px"
        py="8px"
      >
        <InfoOutlinedIcon sx={{ fontSize: 18, color: "var(--green)", flexShrink: 0 }} />
        <Typography
          fontSize="14px"
          fontWeight={500}
          lineHeight="24px"
          color="var(--green)"
          sx={{ letterSpacing: 0 }}
        >
          {t("MyData-Banner-Financial", { lender: data?.lender ?? "—" })}
        </Typography>
      </Box>

      {/* Subtitle + lender chip */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap="8px"
      >
        <Typography fontSize="14px" fontWeight={500} color="var(--gray1)" sx={{ letterSpacing: 0 }}>
          {t("MyData-Context")}
        </Typography>
        <Box display="flex" alignItems="center" gap="16px">
          <Typography fontSize="14px" fontWeight={500} color="var(--gray1)" sx={{ letterSpacing: 0 }}>
            {t("MyData-Lender")}:
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            gap="8px"
            px="12px"
            py="4px"
            bgcolor="var(--white)"
            border="1px solid var(--green)"
            borderRadius="4px"
          >
            <Box
              width={24}
              height={24}
              borderRadius="50%"
              bgcolor="var(--green)"
              color="var(--white)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              sx={{ fontSize: "10px", fontWeight: 700 }}
            >
              {initials(data?.lender)}
            </Box>
            <Typography fontSize="14px" fontWeight={500} color="var(--green)" sx={{ letterSpacing: 0 }}>
              {data?.lender ?? "—"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Table card */}
      <Box display="flex" flexDirection="column" gap="16px" bgcolor="var(--white)" borderRadius="8px" p="20px">
        {/* Toolbar: search + year filter */}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="12px">
          <Box
            display="flex"
            alignItems="center"
            gap="8px"
            px="12px"
            py="8px"
            border="1px solid var(--gray5)"
            borderRadius="8px"
            bgcolor="var(--white)"
            width="360px"
            maxWidth="100%"
          >
            <Box component="img" src="/img/search-normal.svg" alt="" width={24} height={24} />
            <Box
              component="input"
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder={t("MyData-Search-Year")}
              sx={{
                flex: 1,
                minWidth: 0,
                border: 0,
                outline: "none",
                background: "transparent",
                fontFamily: "inherit",
                fontSize: "16px",
                lineHeight: "24px",
                color: "var(--dark)",
                "&::placeholder": { color: "var(--gray1)", opacity: 1 },
              }}
            />
          </Box>

          <Box display="flex" alignItems="center" gap="16px" flexWrap="wrap">
            <Select
              value={yearFilter}
              onChange={(e) => {
                const v = e.target.value;
                setYearFilter(v === "all" ? "all" : Number(v));
                setPage(0);
              }}
              sx={{
                minWidth: 140,
                borderRadius: "8px",
                fontSize: "16px",
                color: "var(--dark)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--gray5)" },
                "& .MuiSelect-select": { py: "8px", px: "12px" },
              }}
            >
              <MenuItem value="all">{t("MyData-Filter-Year-All")}</MenuItem>
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* Disclosure table */}
        <Box borderRadius="4px" overflow="hidden">
          <FeTableScroll>
            <FeTable>
              <TableHead>
                <TableRow>
                  <FeHeadCell sx={{ width: 100 }}>{t("Col-Year")}</FeHeadCell>
                  <FeHeadCell>{t("Col-Verifier")}</FeHeadCell>
                  <FeHeadCell>{t("Col-Verified-On")}</FeHeadCell>
                  <FeHeadCell>
                    <Box component="span" display="inline-flex" alignItems="center" gap="4px">
                      {t("Col-Emission")}
                      {/* Hint: what the emission figure is derived from (Figma node 6755:13021). */}
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          <>
                            <Typography
                              component="span"
                              display="block"
                              sx={{ fontSize: "12px", fontWeight: 600, lineHeight: "normal", color: "var(--white)" }}
                            >
                              {t("MyData-Emission-Tooltip-Title")}
                            </Typography>
                            <Typography
                              component="span"
                              display="block"
                              sx={{
                                fontSize: "11px",
                                fontWeight: 400,
                                lineHeight: "normal",
                                color: "var(--tooltip-text-muted)",
                              }}
                            >
                              {t("MyData-Emission-Tooltip-Body")}
                            </Typography>
                          </>
                        }
                        slotProps={{
                          tooltip: {
                            sx: {
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                              maxWidth: "none",
                              px: "12px",
                              py: "8px",
                              borderRadius: "6px",
                              bgcolor: "var(--tooltip-bg)",
                              boxShadow: "0px 2px 8px 0px var(--tooltip-shadow)",
                            },
                          },
                          arrow: { sx: { color: "var(--tooltip-bg)" } },
                        }}
                      >
                        {/* Info badge — 16px translucent-white circle with "i" (Figma node 6754:12740). */}
                        <Box
                          component="span"
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            cursor: "help",
                            width: 16,
                            height: 16,
                            borderRadius: "8px",
                            bgcolor: "var(--info-badge-bg)",
                            color: "var(--white)",
                            fontFamily: "Inter",
                            fontSize: "10px",
                            fontWeight: 500,
                            lineHeight: "normal",
                          }}
                        >
                          i
                        </Box>
                      </Tooltip>
                    </Box>
                  </FeHeadCell>
                  <FeHeadCell>{t("Col-As-Of")}</FeHeadCell>
                  <FeHeadCell sx={{ width: 190 }}>{t("Col-DQS")}</FeHeadCell>
                  <FeHeadCell align="center" sx={{ width: 160 }}>
                    {t("Col-Actions")}
                  </FeHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map((r) => (
                  <FeRow key={r.year}>
                    <FeCell sx={{ fontWeight: 700 }}>{r.year}</FeCell>
                    <FeCell>{r.verifier}</FeCell>
                    <FeCell>{toDmy(r.verifiedOn)}</FeCell>
                    <FeCell>{formatNumber(r.emission)}</FeCell>
                    <FeCell>{toDmy(r.asOfDate)}</FeCell>
                    <FeCell>
                      <DqsBadge dqs={r.dqs} label={t("DQS")} />
                    </FeCell>
                    <FeCell align="center">
                      <IconButton size="small" sx={{ p: "2px" }} onClick={() => setDetailRow(r)}>
                        <Box component="img" src="/img/eye.svg" alt="" width={24} height={24} />
                      </IconButton>
                    </FeCell>
                  </FeRow>
                ))}
                {paged.length === 0 && (
                  <FeRow>
                    <FeCell colSpan={7} align="center" sx={{ color: "var(--gray1)" }}>
                      {t("MyData-No-Rows")}
                    </FeCell>
                  </FeRow>
                )}
              </TableBody>
            </FeTable>
          </FeTableScroll>
          <FePagination
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={(n) => {
              setRowsPerPage(n);
              setPage(0);
            }}
          />
        </Box>
      </Box>

      {/* View Certificate (eye action) — document preview + read-only figures. */}
      <MyDataCertModal
        open={detailRow !== null}
        onClose={() => setDetailRow(null)}
        row={detailRow}
        fileUrl={detailRow?.certificateUrl ?? null}
      />
    </Box>
  );
};

export default MyData;
